import React, { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { UserContext } from '../App';
import { useNomineeEstates } from '../hooks/useNomineeEstates';
import { formatEstateDisplayName } from '../utils/nomineeEstate';
import { Stack, Text } from '../ui';

/**
 * Lets nominees switch which owner estate they are viewing.
 * Always renders a dropdown (even for a single estate) so the control stays discoverable.
 */
export default function NomineeEstateSwitcher({ compact = false }) {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const { estates, loading } = useNomineeEstates();
  const navigate = useNavigate();
  const location = useLocation();

  const routeOwnerSub = useMemo(() => {
    const m = String(location.pathname || '').match(/^\/nominee\/estate\/([^/]+)\/(financial|legal|other)\b/);
    if (!m) return '';
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }, [location.pathname]);

  if (loading) {
    return (
      <Text size="sm" muted style={{ margin: compact ? '0 18px 12px 18px' : 0 }}>
        Loading estates…
      </Text>
    );
  }

  if (estates.length < 1) return null;

  const storedActiveSub = String(currentUser?.activeOwnerSub || '').trim();
  const effectiveActiveSub =
    storedActiveSub
    || routeOwnerSub
    || (estates.length === 1 ? String(estates[0]?.ownerSub || '').trim() : '');

  const onChange = async (nextOwnerSub) => {
    const next = String(nextOwnerSub || '').trim();
    if (!next) return;

    try {
      await api.post('/auth/switch-role', { active_context: 'nominee', active_owner_sub: next });
      const updated = { ...currentUser, activeOwnerSub: next, type: 'nominee' };
      setCurrentUser(updated);
      try { localStorage.setItem('currentUser', JSON.stringify(updated)); } catch {}
    } catch (_) {
      /* non-fatal */
    }

    if (next === storedActiveSub && next === routeOwnerSub) return;

    const tabMatch = String(location.pathname || '').match(
      /^\/nominee\/estate\/[^/]+\/(financial|legal|other)\b/
    );
    if (tabMatch) {
      navigate(`/nominee/estate/${encodeURIComponent(next)}/${tabMatch[1]}`);
    } else {
      navigate('/nominee');
    }
  };

  return (
    <Stack gap={6} style={{ margin: compact ? '0 18px 16px 18px' : undefined }}>
      <Text
        as="label"
        htmlFor="nominee-estate-switcher"
        size="sm"
        weight={600}
        style={{
          margin: 0,
          color: compact ? 'rgba(255,255,255,0.75)' : 'var(--dusk-500)',
          textTransform: compact ? 'uppercase' : undefined,
          letterSpacing: compact ? 0.5 : undefined,
          fontSize: compact ? 11 : undefined,
        }}
      >
        Viewing estate
      </Text>
      <select
        id="nominee-estate-switcher"
        className="pv-select"
        value={effectiveActiveSub}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          maxWidth: compact ? undefined : 520,
          fontSize: compact ? 14 : undefined,
        }}
      >
        {!effectiveActiveSub ? (
          <option value="" disabled>
            Select an estate…
          </option>
        ) : null}
        {estates.map((e) => (
          <option key={e.ownerSub} value={e.ownerSub}>
            {formatEstateDisplayName(e)}
          </option>
        ))}
      </select>
    </Stack>
  );
}
